import React, { useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { FocusContext, setFocus, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { networkLogEnabledAtom, networkLogEntriesAtom, NetworkLogEntry } from '@smart-tv/atoms';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import { isBackKey, isShiftKey } from '@smart-tv/utils/remoteKeys';
import Portal from '@smart-tv/components/portal/portal';
import styles from './network-log-overlay.module.scss';

export const NETWORK_LOG_OVERLAY_FOCUS_KEY = 'NETWORK_LOG_OVERLAY';

let _returnFocusKey: string | null = null;

export function openNetworkLog(returnFocusKey: string) {
  _returnFocusKey = returnFocusKey;
  setFocus(NETWORK_LOG_OVERLAY_FOCUS_KEY);
}

const StatusBadge: React.FC<{ status: NetworkLogEntry['status'] }> = ({ status }) => (
  <span className={`${styles.badge} ${styles[`badge--${status}`]}`}>{status}</span>
);

const ResponseToggle: React.FC<{ response: unknown }> = ({ response }) => {
  const [showResponse, setShowResponse] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);

  const { ref, focused } = useFocusable({
    onEnterPress: () => setShowResponse((v) => !v),
    onArrowPress: (direction) => {
      if (!showResponse || !preRef.current) return true;
      if (direction === 'down') {
        preRef.current.scrollTop += 60;
        return false;
      }
      if (direction === 'up' && preRef.current.scrollTop > 0) {
        preRef.current.scrollTop -= 60;
        return false;
      }
      return true;
    },
  });

  return (
    <>
      <div
        ref={ref}
        onClick={() => setShowResponse((v) => !v)}
        className={`${styles['entry-detail-response-toggle']} ${focused ? styles['entry--focused'] : ''}`}
      >
        {showResponse ? 'Hide response' : 'Show response'}
      </div>
      {showResponse && (
        <pre ref={preRef} className={styles['entry-detail-pre']}>
          {JSON.stringify(response, null, 2)}
        </pre>
      )}
    </>
  );
};

const EntryRow: React.FC<{ entry: NetworkLogEntry }> = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);
  const { ref, focused } = useFocusable({
    onEnterPress: () => setExpanded((v) => !v),
  });

  useEffect(() => {
    if (focused && ref.current) {
      (ref.current as HTMLElement).scrollIntoView({ block: 'nearest' });
    }
  }, [focused, ref]);

  return (
    <>
      <div
        ref={ref}
        onClick={() => setExpanded((v) => !v)}
        className={`${styles.entry} ${focused ? styles['entry--focused'] : ''}`}
      >
        <div className={styles['entry-row']}>
          <span className={styles['entry-id']}>#{entry.id}</span>
          <span className={styles['entry-time']}>{entry.timestamp.slice(11, 23)}</span>
          <span className={styles['entry-cmd']}>{entry.cmd}</span>
          <span className={styles['entry-method']}>{entry.method.toUpperCase()}</span>
          <StatusBadge status={entry.status} />
          {entry.duration !== undefined && (
            <span className={styles['entry-duration']}>{entry.duration}ms</span>
          )}
        </div>
        {expanded && (
          <div className={styles['entry-detail']}>
            {entry.params !== undefined && (
              <>
                <span className={styles['entry-detail-label']}>Params</span>
                <pre className={styles['entry-detail-pre']}>
                  {JSON.stringify(entry.params, null, 2)}
                </pre>
              </>
            )}
            {entry.status === 'error' && entry.error !== undefined && (
              <>
                <span className={styles['entry-detail-label']}>Error</span>
                <pre className={styles['entry-detail-pre']}>
                  {typeof entry.error === 'object' ? JSON.stringify(entry.error, null, 2) : String(entry.error)}
                </pre>
              </>
            )}
          </div>
        )}
      </div>
      {expanded && entry.status === 'success' && entry.response !== undefined && (
        <ResponseToggle response={entry.response} />
      )}
    </>
  );
};

const EmptyPlaceholder: React.FC = () => {
  const { ref, focused } = useFocusable();
  return (
    <div ref={ref} className={`${styles.empty} ${focused ? styles['entry--focused'] : ''}`}>
      Waiting for requests…
    </div>
  );
};

const NetworkLogOverlay: React.FC = () => {
  const enabled = useAtomValue(networkLogEnabledAtom);
  const entries = useAtomValue(networkLogEntriesAtom);
  const listRef = useRef<HTMLDivElement>(null);
  const [shiftHeld, setShiftHeld] = useState(false);

  // Lets the user Shift-click through the overlay to reach elements underneath in browser mode
  useEffect(() => {
    if (!IS_BROWSER) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isShiftKey(e)) setShiftHeld(true);
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (isShiftKey(e)) setShiftHeld(false);
    };
    const handleBlur = () => setShiftHeld(false);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  useEffect(() => {
    const rootElement = document.getElementById('root');
    if (!rootElement) return undefined;

    rootElement.classList.toggle('network-log-overlay-active', enabled);

    return () => {
      rootElement.classList.remove('network-log-overlay-active');
    };
  }, [enabled]);

  const { ref, focusKey, focused, hasFocusedChild } = useFocusable({
    focusKey: NETWORK_LOG_OVERLAY_FOCUS_KEY,
    trackChildren: true,
    isFocusBoundary: true,
    focusable: enabled, // Only allow focusing when enabled
  });

  // Keep a ref that's always current so the capture handler never goes stale
  const overlayHasFocusRef = useRef(false);
  overlayHasFocusRef.current = focused || hasFocusedChild;

  // Register once — read current state via ref
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!overlayHasFocusRef.current) return;
      if (isBackKey(e)) {
        e.stopImmediatePropagation();
        if (_returnFocusKey) setFocus(_returnFocusKey);
      }
    };
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to top (newest entry) whenever a new entry arrives
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [entries.length]);

  if (!enabled) return null;

  return (
    <Portal>
      <FocusContext.Provider value={focusKey}>
        <div
          ref={ref}
          className={`${styles.overlay} ${hasFocusedChild ? styles['overlay--focused'] : ''} ${
            IS_BROWSER ? styles['overlay--browser'] : ''
          } ${IS_BROWSER && shiftHeld ? styles['overlay--shift-passthrough'] : ''}`}
        >
          <div className={styles.list} ref={listRef}>
            {entries.length === 0 ? (
              <EmptyPlaceholder />
            ) : (
              entries.map((entry) => <EntryRow key={entry.id} entry={entry} />)
            )}
          </div>
        </div>
      </FocusContext.Provider>
    </Portal>
  );
};

export default NetworkLogOverlay;
